import { inject } from 'mobx-react';
import * as React from 'react';

import { RouteStore } from 'bernie-core';

import { MarketingFeeDetails, PriceBreakDownViewModel, PriceViewModel, RecentlyViewedModel } from 'models';

import { LeadAndSubPrice } from 'components/common/pricing/lead-and-sub-price';
import { LeadPrice } from './lead-price';
import { MarketingFeeDetailsContainer } from './marketing-fee-details-container';
import { PriceBreakDown } from './price-breakdown';
import { PriceMessagesContainer } from './price-messages-container';
import { StrikeoutPriceAndInfoContainer } from './strikeout-price-and-info-container';
import { Text } from 'components/common/text';

export interface ChildActionsOptions {
  trackingRfrr?: string;
  className?: string;
}

export interface PriceProps {
  className: string | string[];
  diff?: string;
  messages?: string[];
  price: PriceViewModel;
  alignmentClasses?: string;
  routeStore?: RouteStore;
  hotelId?: number;
  priceFontWeight: string;
  wrapPrice?: boolean;
  id?: string;
  recentlyViewed?: RecentlyViewedModel;
  excludePriceBreakDownLink?: boolean;
  priceBreakDownModel: PriceBreakDownViewModel[];
  roomName?: string;
  ratePlanCode: string;
  childActions?: (options: ChildActionsOptions) => React.ReactNode;
}

interface AlignmentClasses {
  alignmentClasses?: string;
  query?: any;
  dialogId?: string;
  handleClose?: () => void;
  priceFontWeight: string;
  wrapPrice?: boolean;
  dialogType?: string;
  marketingFeeQuery: any;
  marketingFeeDialogId: string;
  marketingFeeDetails?: MarketingFeeDetails;
  viewed?: boolean;
  excludePriceBreakDownLink?: boolean;
}

type PriceViewModelWithAlignment = PriceViewModel & AlignmentClasses;

@inject('routeStore')
export class Price extends React.Component<PriceProps, {}> {
  constructor(props) {
    super(props);

    this.routeStoreGoBack = this.routeStoreGoBack.bind(this);
  }

  public static defaultProps: Partial<PriceProps> = {
    alignmentClasses: 'all-grid-align-middle all-grid-align-end',
    excludePriceBreakDownLink: false
  };

  public render() {
    const {
      alignmentClasses,
      className,
      price,
      messages,
      diff,
      routeStore,
      hotelId,
      priceFontWeight,
      wrapPrice,
      id,
      recentlyViewed,
      excludePriceBreakDownLink,
      priceBreakDownModel,
      roomName,
      ratePlanCode,
      childActions
    } = this.props;

    if (!price) {
      return null;
    }

    const priceMessage = price && price.priceMessage;
    const pricelength = Object.keys(price).length; // for unavailable rateplan.
    const hasAdditionalMessages = messages && messages.length > 0;

    const isPwaDialogNested = id === 'infosite-room-info-dialog-ratePlans';
    const dialogId = hotelId ? `priceDisclaimerDialog-${hotelId}` : 'priceDisclaimerDialog';
    const pwaDialog = isPwaDialogNested ? { pwaDialogNested: dialogId } : { pwaDialog: dialogId };
    const dialogType = isPwaDialogNested ? 'pwaDialogNested' : 'pwaDialog';
    const dialogQuery = routeStore.mergeQueryString(routeStore.search, pwaDialog);
    const query = { pathname: routeStore.pathname, search: dialogQuery };

    const marketingFeeDialogId = hotelId ? `priceMarketingFeeDialog-${hotelId}` : 'priceMarketingFeeDialog';
    const marketingFeeDialogQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialog: marketingFeeDialogId });
    const marketingFeeQuery = { pathname: routeStore.pathname, search: marketingFeeDialogQuery };
    const viewed = recentlyViewed != null;
    const layerId = ratePlanCode ? ratePlanCode : hotelId;

    return (
      <div data-stid="content-hotel-price" className={`price ${className || ''}`}>
        {!priceMessage && diff && <PriceDiff price={price} diff={diff} alignmentClasses={alignmentClasses} />}
        {!priceMessage && !diff && pricelength !== 1 && (
          <PriceValues
            {...price}
            {...{
              alignmentClasses,
              query,
              dialogId,
              priceFontWeight,
              wrapPrice,
              dialogType,
              marketingFeeDialogId,
              marketingFeeQuery,
              viewed
            }}
            handleClose={this.routeStoreGoBack}
          />
        )}
        {hasAdditionalMessages && <RenderSupportingMessages messages={messages} />}
        {!excludePriceBreakDownLink && priceBreakDownModel && priceBreakDownModel.length > 0 && (
          <PriceBreakDown
            priceBreakDownModel={priceBreakDownModel}
            id={id}
            roomName={roomName}
            layerId={layerId}
            childActions={childActions}
          />
        )}
        {price && pricelength === 1 && <PriceDescription text={price.lead} theme={Text.theme.URGENCY} />}
        {priceMessage && <PriceDescription {...priceMessage} />}
      </div>
    );
  }

  private routeStoreGoBack() {
    this.props.routeStore.goBack();
  }
}
const RenderSupportingMessages = ({ messages }) => (
  <>
    {messages &&
      messages.length > 0 &&
      messages.map((message, index) => (
        <div
          className={`uitk-cell pwa-theme--grey-700 uitk-type-100 ${index === 0 ? 'uitk-type-bold' : ''}`}
          data-stid={`supporting-messages-${index}`}
          key={`supporting-messages-${index}`}
        >
          {message}
        </div>
      ))}
  </>
);

const PriceDiff = ({ price, diff, alignmentClasses }) => {
  const { lead, label, numberOfNightsAndRooms } = price;

  return (
    <React.Fragment>
      <div className={`uitk-grid ${alignmentClasses}`}>
        <span className="uitk-cell all-cell-shrink uitk-type-500 uitk-type-bold" data-stid="content-hotel-display-price">
          {diff}
        </span>
      </div>
      {lead && label && (
        <span className="uitk-type-100 uitk-type-medium pwa-theme--grey-700" data-stid="content-hotel-lead-label">
          {lead}
          {label}
        </span>
      )}
      {numberOfNightsAndRooms && (
        <div
          className="numberOfNightsAndRooms pwa-theme--grey-700 uitk-type-100 uitk-type-regular"
          data-stid="content-hotel-number-nights-rooms"
        >
          {numberOfNightsAndRooms}
        </div>
      )}
    </React.Fragment>
  );
};
PriceDiff['displayName'] = 'PriceDiff';

const PriceValues = ({
  lead,
  strikeOut,
  label,
  numberOfNightsAndRooms,
  alignmentClasses,
  strikeoutDisclaimer,
  query,
  dialogId,
  handleClose,
  priceFontWeight,
  wrapPrice,
  dialogType,
  marketingFeeQuery,
  marketingFeeDialogId,
  marketingFeeDetails,
  viewed,
  priceMessages,
  options
}: PriceViewModelWithAlignment) => (
  <>
    {options && options.length > 0 && (
      <div className={`uitk-grid ${alignmentClasses}`}>
        {options.map((option, index) => (
          <LeadAndSubPrice
            key={index}
            {...{ option, strikeoutDisclaimer, query, dialogId, dialogType, handleClose, wrapPrice, priceFontWeight, viewed, label }}
          />
        ))}
      </div>
    )}
    {(!options || options.length === 0) && (
      <div className={`uitk-grid ${alignmentClasses}`}>
        {strikeOut && (
          <StrikeoutPriceAndInfoContainer {...{ strikeoutDisclaimer, query, strikeOut, dialogId, handleClose, wrapPrice, dialogType }} />
        )}
        <LeadPrice {...{ label, lead, priceFontWeight, wrapPrice, strikeOut, viewed }} />
      </div>
    )}

    <PriceMessagesContainer {...{ priceMessages, numberOfNightsAndRooms }} />

    <MarketingFeeDetailsContainer {...{ marketingFeeDetails, marketingFeeQuery, marketingFeeDialogId, handleClose, alignmentClasses }} />
  </>
);
PriceValues['displayName'] = 'PriceValues';

const PriceDescription = ({ text, theme }: PriceViewModel['priceMessage']) => {
  const themeValue = Text.theme[theme] ? Text.theme[theme] : theme; // TODO: Can remove once we switch to BEX API

  return (
    text && (
      <span className={'uitk-type-300 uitk-type-medium'} data-stid="content-hotel-sold-out">
        <Text theme={themeValue}>{text}</Text>
      </span>
    )
  );
};
PriceDescription['displayName'] = 'PriceDescription';
